import React, { useEffect, useMemo, useState } from "react";
import { api, apiJson } from "@/api/client.js";
import { DANIEL_DESSERTS, HUNT_SUPPERS, JOSEE_FOOD } from "@/content/huntMeals.js";
import { MealExtras, SepaqInscription } from "@/components/HuntMorning.jsx";
import { HUMOUR_ANECDOTE, HUMOUR_JOKES, KETCHUP_IMAGE } from "@/content/humourGang.js";
import { GROUP_PLAN, SEPAQ_OFFICIAL } from "@/content/sepaqHunt2026.js";
import { PREP_PHOTO_CANDIDATES } from "@/content/prepPhotos.js";
import { TASK_CANDIDATES } from "@/content/taskCandidates.js";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";

const DONE = new Set(["ready", "loaded", "confirmed", "done"]);
const APPROVED_KEY = "og-candidates-approved";

export function isLiveTask(task) {
  const title = task?.title || "";
  if (/^historique/i.test(title)) return false;
  if (/\b(test|qa|robot)\b|waypoint de test/i.test(title)) return false;
  return true;
}

function nearKey(title) {
  return String(title || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\d{5,}/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function openCount(tasks, memberId) {
  return tasks.filter((task) => isLiveTask(task) && task.assignee_id === memberId && task.status !== "done").length;
}

export function TaskBoard({ tasks, memberId, hunters, admin, meId, unknown, work, onToggle, onCreate, onAssign, onDelete, onRename, onRestore }) {
  const [title, setTitle] = useState("");
  const [who, setWho] = useState(memberId || meId || hunters[0]?.id || "");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState("");
  const [draft, setDraft] = useState("");
  const [approved, setApproved] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(APPROVED_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [trash, setTrash] = useState(null);
  const rows = tasks.filter((task) => isLiveTask(task) && (!memberId || task.assignee_id === memberId));
  const todo = rows.filter((task) => task.status !== "done");
  const done = rows.filter((task) => task.status === "done");
  const duplicates = useMemo(() => {
    if (!admin || memberId) return [];
    const groups = new Map();
    rows.forEach((task) => {
      const key = `${nearKey(task.title)}|${task.assignee_id || ""}`;
      if (!nearKey(task.title)) return;
      const list = groups.get(key) || [];
      list.push(task);
      groups.set(key, list);
    });
    return [...groups.values()].filter((list) => list.length > 1);
  }, [admin, memberId, rows]);
  const candidates = TASK_CANDIDATES.filter((item) => !approved.includes(item.id));
  const duplicateIds = new Set(duplicates.flat().map((task) => task.id));
  const todoRows = todo.filter((task) => !duplicateIds.has(task.id));
  const doneRows = done.filter((task) => !duplicateIds.has(task.id));

  function canCheck(task) {
    return admin || task.assignee_id === meId;
  }

  function approve(item) {
    onCreate(item.proposedTitle, item.assigneeId || meId);
    const next = [...approved, item.id];
    setApproved(next);
    localStorage.setItem(APPROVED_KEY, JSON.stringify(next));
  }

  async function loadTrash() {
    try {
      const body = await api("/api/tasks/trash");
      setTrash(body?.items || []);
    } catch {
      setTrash([]);
    }
  }

  const counter = todo.length === 0
    ? work.allDone
    : todo.length === 1
      ? work.oneThing
      : work.nThings.replace("{n}", String(todo.length));

  return (
    <div className={`og-taskboard${memberId ? " og-taskboard--person" : ""}`} data-testid="task-board">
      {memberId ? <p className="og-taskcount" data-testid="task-count">{counter}</p> : null}
      {admin ? (
        <button type="button" className="og-tile og-taskadd" data-testid="task-add-open" onClick={() => setAdding(true)}>+ {work.addTask}</button>
      ) : null}
      {admin && adding ? (
        <form
          className="og-task-compose"
          data-testid="task-compose"
          onSubmit={(event) => {
            event.preventDefault();
            const next = title.trim();
            if (!next) return;
            onCreate(next, who);
            setTitle("");
            setAdding(false);
          }}
        >
          <label>
            {work.taskPlaceholder}?
            <input
              className="og-input"
              value={title}
              aria-label={work.taskPlaceholder}
              data-testid="task-title"
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <label>
            Pour qui ?
            <select className="og-input" aria-label={work.person} data-testid="task-for" value={who} onChange={(event) => setWho(event.target.value)}>
              {hunters.map((hunter) => (
                <option key={hunter.id} value={hunter.id}>{hunter.first_name}</option>
              ))}
            </select>
          </label>
          <button className="og-btn og-btn--primary" type="submit" data-testid="task-add">{work.save}</button>
        </form>
      ) : null}
      {duplicates.length ? (
        <section className="og-admin-block" data-testid="task-duplicates">
          <h3>{work.duplicates}</h3>
          {duplicates.map((group) => (
            <div key={group[0].id}>
              {group.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  done={task.status === "done"}
                  canCheck={canCheck(task)}
                  admin={admin}
                  hunters={hunters}
                  work={work}
                  editing={editing}
                  draft={draft}
                  onToggle={onToggle}
                  onAssign={onAssign}
                  onDelete={onDelete}
                  onStartEdit={(task) => { setEditing(task.id); setDraft(task.title); }}
                  onDraft={setDraft}
                  onRename={onRename}
                  onCancelEdit={() => setEditing("")}
                />
              ))}
            </div>
          ))}
        </section>
      ) : null}
      <div className="og-taskboard__cols">
        <section>
          <h3>{work.todo}</h3>
          {todoRows.length ? todoRows.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              done={false}
              canCheck={canCheck(task)}
              admin={admin}
              hunters={hunters}
              work={work}
              editing={editing}
              draft={draft}
              onToggle={onToggle}
              onAssign={onAssign}
              onDelete={onDelete}
              onStartEdit={(task) => { setEditing(task.id); setDraft(task.title); }}
              onDraft={setDraft}
              onRename={onRename}
              onCancelEdit={() => setEditing("")}
            />
          )) : (memberId ? null : <p className="og-hint">{unknown}</p>)}
        </section>
        <section>
          <h3>{work.done}</h3>
          {doneRows.length ? doneRows.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              done
              canCheck={canCheck(task)}
              admin={admin}
              hunters={hunters}
              work={work}
              editing={editing}
              draft={draft}
              onToggle={onToggle}
              onAssign={onAssign}
              onDelete={onDelete}
              onStartEdit={(task) => { setEditing(task.id); setDraft(task.title); }}
              onDraft={setDraft}
              onRename={onRename}
              onCancelEdit={() => setEditing("")}
            />
          )) : (memberId ? null : <p className="og-hint">{unknown}</p>)}
        </section>
      </div>
      {admin && !memberId ? (
        <section className="og-admin-block" data-testid="task-candidates">
          <h3>{work.candidates}</h3>
          {candidates.map((item) => (
            <article key={item.id} className="og-candidate" data-testid={`candidate-${item.id}`}>
              <strong>{item.proposedTitle}</strong>
              <p>{item.author} · {item.when} · {item.confidence}</p>
              <p>{item.original}</p>
              {item.note ? <p>{item.note}</p> : null}
              <button type="button" className="og-admin-btn" data-testid={`task-approve-${item.id}`} onClick={() => approve(item)}>{work.approve}</button>
            </article>
          ))}
          <button type="button" className="og-admin-btn" data-testid="task-trash-open" onClick={loadTrash}>{work.trash}</button>
          {trash ? (
            <ul data-testid="task-trash">
              {trash.length ? trash.map((task) => (
                <li key={task.id}>
                  {task.title}
                  <button type="button" className="og-admin-btn" data-testid={`task-restore-${task.id}`} onClick={async () => { await onRestore(task); setTrash((rows) => (rows || []).filter((row) => row.id !== task.id)); }}>{work.restore}</button>
                </li>
              )) : <li>{unknown}</li>}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function TaskRow({ task, done, canCheck, admin, hunters, work, editing, draft, onToggle, onAssign, onDelete, onStartEdit, onDraft, onRename, onCancelEdit }) {
  const [open, setOpen] = useState(false);
  const [reassign, setReassign] = useState(false);
  const isEditing = editing === task.id;
  return (
    <div className="og-taskrow" data-testid={`task-row-${task.id}`}>
      <label>
        <input
          type="checkbox"
          checked={done}
          disabled={!canCheck}
          data-testid={`task-check-${task.id}`}
          onChange={() => onToggle(task)}
        />
        {isEditing ? (
          <input
            className="og-input"
            value={draft}
            aria-label={work.edit}
            data-testid={`task-edit-input-${task.id}`}
            onChange={(event) => onDraft(event.target.value)}
          />
        ) : (
          <span>{task.title}</span>
        )}
      </label>
      {isEditing ? (
        <button type="button" className="og-admin-btn" data-testid={`task-save-${task.id}`} onClick={() => { onRename(task, draft); onCancelEdit(); }}>{work.save}</button>
      ) : null}
      {admin && reassign ? (
        <select
          className="og-input"
          aria-label={task.title}
          value={task.assignee_id || ""}
          data-testid={`task-assign-${task.id}`}
          onChange={(event) => { onAssign(task, event.target.value); setReassign(false); }}
        >
          {hunters.map((hunter) => (
            <option key={hunter.id} value={hunter.id}>{hunter.first_name}</option>
          ))}
        </select>
      ) : null}
      {admin ? (
        <span className="og-taskmenu">
          <button type="button" className="og-taskmenu__btn" data-testid={`task-menu-${task.id}`} aria-label={work.edit} onClick={() => setOpen((value) => !value)}>⋯</button>
          {open ? (
            <span className="og-taskmenu__list">
              <button type="button" data-testid={`task-edit-${task.id}`} onClick={() => { onStartEdit(task); setOpen(false); }}>{work.edit}</button>
              <button type="button" data-testid={`task-reassign-${task.id}`} onClick={() => { setReassign(true); setOpen(false); }}>{work.reassign}</button>
              {done ? <button type="button" data-testid={`task-reopen-${task.id}`} onClick={() => { onToggle(task); setOpen(false); }}>{work.reopen}</button> : null}
              <button type="button" data-testid={`task-delete-${task.id}`} onClick={() => onDelete(task)}>{work.remove}</button>
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

export function MealsPanel({ admin = false }) {
  const { ui } = useOrignalUi();
  const work = ui.hunt.work;
  const [meals, setMeals] = useState({});
  const [editing, setEditing] = useState("");
  const [draft, setDraft] = useState("");

  useEffect(() => {
    api("/api/overrides")
      .then((body) => {
        if (body?.meals && typeof body.meals === "object" && !Array.isArray(body.meals)) setMeals(body.meals);
      })
      .catch(() => {});
  }, []);

  async function saveDish(evening) {
    const next = { ...meals, [evening]: { ...(meals[evening] || {}), dish: draft } };
    setMeals(next);
    setEditing("");
    try {
      await apiJson("/api/overrides", { meals: next }, "PUT");
    } catch {
      /* la correction reste visible dans cette séance */
    }
  }

  return (
    <div data-testid="meals-panel">
      <ul className="og-meal-list">
        {HUNT_SUPPERS.map((supper) => {
          const dish = meals[supper.evening]?.dish || supper.dish;
          return (
            <li key={supper.evening} data-testid={`supper-${supper.evening}`}>
              <strong>Soir {supper.evening} — {supper.who}</strong>
              {editing === String(supper.evening) ? (
                <input
                  className="og-input"
                  value={draft}
                  aria-label={supper.who}
                  data-testid={`supper-edit-${supper.evening}`}
                  onChange={(event) => setDraft(event.target.value)}
                />
              ) : (
                <span>{dish}</span>
              )}
              {admin ? (
                editing === String(supper.evening) ? (
                  <button type="button" className="og-admin-btn" data-testid={`supper-save-${supper.evening}`} onClick={() => saveDish(String(supper.evening))}>{work.save}</button>
                ) : (
                  <button type="button" className="og-admin-btn" data-testid={`supper-edit-${supper.evening}`} onClick={() => { setEditing(String(supper.evening)); setDraft(dish); }}>{work.edit}</button>
                )
              ) : null}
            </li>
          );
        })}
      </ul>
      <p data-testid="supper-pierre-check">{work.pierreCheck}</p>
      <section className="og-meal-side" data-testid="josee-food">
        <h3>{JOSEE_FOOD.who}</h3>
        <p>{JOSEE_FOOD.note}</p>
        <ul>
          {JOSEE_FOOD.items.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </section>
      <MealExtras />
      <section className="og-meal-side" data-testid="daniel-desserts">
        <h3>{DANIEL_DESSERTS.who}</h3>
        <ul>
          {DANIEL_DESSERTS.items.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </section>
    </div>
  );
}

export function NotesPanel({ admin = false, unknown }) {
  const { ui } = useOrignalUi();
  const work = ui.hunt.work;
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");

  useEffect(() => {
    api("/api/overrides")
      .then((body) => {
        if (Array.isArray(body?.notes)) setNotes(body.notes);
      })
      .catch(() => {});
  }, []);

  async function persist(next) {
    setNotes(next);
    try {
      await apiJson("/api/overrides", { notes: next }, "PUT");
    } catch {
      /* la note reste dans cette séance */
    }
  }

  return (
    <div data-testid="notes-panel">
      {notes.length ? notes.map((note) => (
        <p key={note.id} data-testid={`note-${note.id}`}>
          <span>{note.text}</span>
          {admin ? (
            <button
              type="button"
              className="og-admin-btn og-admin-btn--danger"
              data-testid={`note-delete-${note.id}`}
              onClick={() => {
                if (!window.confirm(work.confirmDelete)) return;
                persist(notes.filter((row) => row.id !== note.id));
              }}
            >
              {work.remove}
            </button>
          ) : null}
        </p>
      )) : <p data-testid="notes-pending">{unknown}</p>}
      {admin ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const next = text.trim();
            if (!next) return;
            persist([...notes, { id: `note_${Date.now()}`, text: next }]);
            setText("");
          }}
        >
          <input className="og-input" value={text} data-testid="note-text" aria-label={work.notes} onChange={(event) => setText(event.target.value)} />
          <button className="og-btn og-btn--primary" type="submit" data-testid="note-add">{work.addNote}</button>
        </form>
      ) : null}
    </div>
  );
}

export function HumourPanel({ work, admin = false }) {
  const [hidden, setHidden] = useState([]);
  useEffect(() => {
    api("/api/overrides")
      .then((body) => {
        if (Array.isArray(body?.hidden_jokes)) setHidden(body.hidden_jokes);
      })
      .catch(() => {});
  }, []);

  async function hide(id) {
    if (!window.confirm(work.confirmDelete)) return;
    const next = [...hidden, id];
    setHidden(next);
    try {
      await apiJson("/api/overrides", { hidden_jokes: next }, "PUT");
    } catch {
      /* masqué dans cette séance */
    }
  }

  const jokes = HUMOUR_JOKES.filter((joke) => !hidden.includes(joke.id));
  return (
    <div data-testid="humour-panel">
      <h3>{work.jokes}</h3>
      {jokes.map((joke) => (
        <article key={joke.id} className="og-meal-side" data-testid={`joke-${joke.id}`}>
          <p>{joke.when}{joke.status ? ` · ${joke.status}` : ""}</p>
          {joke.lines.map((line) => (
            <p key={line.text}><strong>{line.who}</strong> — {line.text}</p>
          ))}
          {admin ? <button type="button" className="og-admin-btn og-admin-btn--danger" data-testid={`joke-delete-${joke.id}`} onClick={() => hide(joke.id)}>{work.remove}</button> : null}
        </article>
      ))}
      <h3>{work.anecdotes}</h3>
      <article className="og-meal-side" data-testid="anecdote-plume">
        <p>{HUMOUR_ANECDOTE.who} · {HUMOUR_ANECDOTE.when}</p>
        <p className="og-anecdote">{HUMOUR_ANECDOTE.text}</p>
      </article>
      <h3>{work.imageJoke}</h3>
      <p data-testid="ketchup-pending">{KETCHUP_IMAGE.status}. {KETCHUP_IMAGE.note}</p>
    </div>
  );
}

export function PrepPhotoPanel({ work }) {
  return (
    <div data-testid="prep-photo-panel">
      <p>{work.photoValidate}</p>
      <ul className="og-cand-list">
        {PREP_PHOTO_CANDIDATES.map((photo) => (
          <li key={photo.id}>
            <img src={photo.src} alt="" />
            <span>{photo.when} · {photo.sentBy}</span>
            <span>{photo.note}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function VehiclesPanel({ unknown, work }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api("/api/vehicles").then((body) => setItems(body.items || [])).catch(() => setItems([]));
  }, []);
  if (!items) return <p className="og-hint">{unknown}</p>;
  const confirmed = items.filter((vehicle) => !/^véhicule\s+\d+$/i.test(vehicle.name || ""));
  if (!confirmed.length) return <p data-testid="vehicles-empty">{work.askMarcel}</p>;
  return (
    <ul className="og-meal-list" data-testid="vehicles-panel">
      {confirmed.map((vehicle) => {
        const ready = DONE.has(vehicle.status);
        return (
          <li key={vehicle.id} data-testid={`vehicle-${vehicle.id}`}>
            <strong>{vehicle.name}</strong>
            <span>{work.driver} · {unknown}</span>
            <span>{work.linkedGear} · {vehicle.equipment || unknown}</span>
            <span>{ready ? work.ready : work.todo}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function GearPanel({ hunters, unknown, work }) {
  const [items, setItems] = useState(null);
  const [person, setPerson] = useState("all");
  useEffect(() => {
    api("/api/trips/trip_terrain_3j/preparation").then((body) => setItems(body.items || [])).catch(() => setItems([]));
  }, []);
  const rows = useMemo(() => {
    const list = (items || []).filter((item) => item.status !== "not_needed" && item.assignee_id);
    if (person === "all") return list;
    return list.filter((item) => item.assignee_id === person);
  }, [items, person]);
  const todo = rows.filter((item) => !DONE.has(item.status));
  const done = rows.filter((item) => DONE.has(item.status));
  return (
    <div data-testid="gear-panel">
      <label className="og-taskboard__add">
        <span>{work.person}</span>
        <select className="og-input" data-testid="gear-person" value={person} onChange={(event) => setPerson(event.target.value)}>
          <option value="all">{work.everyone}</option>
          {hunters.map((hunter) => (
            <option key={hunter.id} value={hunter.id}>{hunter.first_name}</option>
          ))}
        </select>
      </label>
      {!items ? <p className="og-hint">{unknown}</p> : (
        <div className="og-taskboard__cols">
          <section>
            <h3>{work.bring}</h3>
            {todo.length ? todo.map((item) => <p key={item.id}>{item.name}</p>) : <p>{unknown}</p>}
          </section>
          <section>
            <h3>{work.done}</h3>
            {done.length ? done.map((item) => <p key={item.id}>{item.name}</p>) : <p>{unknown}</p>}
          </section>
        </div>
      )}
    </div>
  );
}

export function OrganizationPanel({ onOpenPdf }) {
  const { ui } = useOrignalUi();
  const s = ui.hunt.work;
  const doc = SEPAQ_OFFICIAL;
  return (
    <div data-testid="org-panel" className="og-org">
      <p className="og-org__sub" data-testid="org-subtitle">{s.orgSubtitle}</p>

      <section className="og-org-group" data-testid="org-group">
        <h3>{s.groupInfo}</h3>
        <p data-testid="org-depart"><strong>{s.departHeading}</strong> {GROUP_PLAN.depart}</p>
        <p data-testid="org-arrive"><strong>{s.arriveHeading}</strong> {GROUP_PLAN.arrive}</p>
        <p data-testid="org-drive">{GROUP_PLAN.drive}</p>
      </section>

      <section data-testid="org-official">
        <h3>{s.sepaqOfficial}</h3>
        <h4>{s.registration}</h4>
        <p data-testid="org-address">{doc.place}<br />{doc.street}<br />{doc.city}<br />{doc.postal}</p>
        <p data-testid="org-hours">{doc.hours}</p>
        <p data-testid="org-sector">{doc.sector}</p>
        <p data-testid="org-meeting">{doc.meeting}</p>
        <p data-testid="org-phone">{doc.phone}</p>
      </section>

      <section data-testid="org-cabin-block">
        <h3>{s.cabin}</h3>
        <p>{doc.cabin}</p>
      </section>

      <section data-testid="org-hunt-block">
        <h3>{s.huntSection}</h3>
        <p data-testid="org-hunt-start">{doc.huntStart}</p>
        <p data-testid="org-hunt-end">{doc.huntEnd}</p>
        <p data-testid="org-checkout">{doc.checkout}</p>
        <p>{doc.game}</p>
      </section>

      <ol className="og-org-time" data-testid="org-timeline">
        {doc.timeline.map((line) => <li key={line}>{line}</li>)}
      </ol>

      <section data-testid="org-services">
        <h3>{s.services}</h3>
        <h4>{s.onSite}</h4>
        <ul>{doc.onSite.map((line) => <li key={line}>{line}</li>)}</ul>
        <h4>{s.nearby}</h4>
        <ul>
          {doc.nearby.map((row) => (
            <li key={row.place}><strong>{row.place}</strong> — {row.items} — {row.distance}</li>
          ))}
        </ul>
      </section>

      <section data-testid="org-rules">
        <h3>{s.rules}</h3>
        <ul>{doc.rules.map((line) => <li key={line}>{line}</li>)}</ul>
      </section>

      <section className="og-org-confirm" data-testid="org-confirm">
        <h3>{s.confirmTitle}</h3>
        <p>{doc.confirm}</p>
        <p>{s.confirmStatus}</p>
      </section>

      <SepaqInscription />
      <button type="button" className="og-tile" data-testid="sepaq-pdf-open" onClick={onOpenPdf}>
        {s.officialDoc}
      </button>
    </div>
  );
}

export async function saveTask(path, body, method) {
  return apiJson(path, body, method);
}
